/**
 * Error Handler Utility
 *
 * Centralized error handling for BDUDS game:
 * - Error categorization and user-friendly messages
 * - Session validation (invalid URLs, expired sessions)
 * - Connection error handling
 * - Retry logic with exponential backoff
 * - Error logging for debugging
 *
 * Phase 6.3: Error Handling & Edge Cases
 */

import { COPY } from '../copy';

/**
 * Error types for categorization
 */
export const ErrorType = {
  INVALID_SESSION: 'INVALID_SESSION',
  EXPIRED_SESSION: 'EXPIRED_SESSION',
  FULL_SESSION: 'FULL_SESSION',
  CONNECTION_LOST: 'CONNECTION_LOST',
  NETWORK_ERROR: 'NETWORK_ERROR',
  GENERIC_ERROR: 'GENERIC_ERROR',
};

/**
 * Session expiration time (24 hours in milliseconds)
 */
const SESSION_EXPIRATION_MS = 24 * 60 * 60 * 1000;

/**
 * Retry configuration
 */
const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY = 1000; // 1 second
const MAX_RETRY_DELAY = 30000; // 30 seconds
const RETRY_BACKOFF_MULTIPLIER = 2;

/**
 * Validate session ID format
 *
 * Accepts UUID or nanoid formats
 * - UUID: 8-4-4-4-12 format with hyphens
 * - nanoid: 21 characters, alphanumeric with hyphens and underscores
 *
 * @param {string} sessionId - Session ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateSessionId(sessionId) {
  // Check for null/undefined/empty
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    return false;
  }

  // Check length constraints (4-100 characters)
  if (sessionId.length < 4 || sessionId.length > 100) {
    return false;
  }

  // UUID format: 8-4-4-4-12
  const uuidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(sessionId)) {
    return true;
  }

  // nanoid format: alphanumeric, hyphens, underscores
  const nanoidPattern = /^[a-zA-Z0-9_-]+$/;
  if (nanoidPattern.test(sessionId)) {
    return true;
  }

  // Invalid format
  return false;
}

/**
 * Check if a session has expired (>24 hours old)
 *
 * @param {string} createdAt - ISO timestamp of session creation
 * @returns {boolean} True if expired, false otherwise
 */
export function isSessionExpired(createdAt) {
  // Invalid timestamp is considered expired
  if (!createdAt || typeof createdAt !== 'string') {
    return true;
  }

  try {
    const created = new Date(createdAt);
    const now = new Date();

    // Check if date is valid
    if (isNaN(created.getTime())) {
      return true;
    }

    const ageMs = now.getTime() - created.getTime();
    return ageMs > SESSION_EXPIRATION_MS;
  } catch (error) {
    console.error('[ErrorHandler] Error checking session expiration:', error);
    return true; // Treat parsing errors as expired
  }
}

/**
 * Get user-friendly error message for error type
 *
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {string} User-friendly error message
 */
export function getErrorMessage(errorType) {
  const messages = {
    [ErrorType.INVALID_SESSION]: COPY.errors.handler.invalidSession,
    [ErrorType.EXPIRED_SESSION]: COPY.errors.handler.expiredSession,
    [ErrorType.FULL_SESSION]: COPY.errors.handler.fullGame,
    [ErrorType.CONNECTION_LOST]: COPY.errors.handler.connectionLost,
    [ErrorType.NETWORK_ERROR]: COPY.errors.handler.networkProblem,
    [ErrorType.GENERIC_ERROR]: COPY.errors.handler.unexpected,
  };

  return messages[errorType] || messages[ErrorType.GENERIC_ERROR];
}

/**
 * Get actionable next step for error type
 *
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {string} Action message for user
 */
export function getErrorAction(errorType) {
  const actions = {
    [ErrorType.INVALID_SESSION]: 'Return to home page and check the link',
    [ErrorType.EXPIRED_SESSION]: 'Create a new game session',
    [ErrorType.FULL_SESSION]: 'Create a new game session',
    [ErrorType.CONNECTION_LOST]: 'Waiting to reconnect automatically',
    [ErrorType.NETWORK_ERROR]: 'Refresh the page or check your connection',
    [ErrorType.GENERIC_ERROR]: 'Try refreshing the page',
  };

  return actions[errorType] || actions[ErrorType.GENERIC_ERROR];
}

/**
 * Check if error type should trigger retry
 *
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {boolean} True if should retry
 */
export function shouldRetry(errorType) {
  const retryableErrors = [
    ErrorType.CONNECTION_LOST,
    ErrorType.NETWORK_ERROR,
    ErrorType.GENERIC_ERROR,
  ];

  return retryableErrors.includes(errorType);
}

/**
 * Check if error is recoverable
 *
 * @param {string} errorType - Error type from ErrorType enum
 * @returns {boolean} True if recoverable
 */
export function isRecoverableError(errorType) {
  const recoverableErrors = [
    ErrorType.CONNECTION_LOST,
    ErrorType.NETWORK_ERROR,
    ErrorType.GENERIC_ERROR,
  ];

  return recoverableErrors.includes(errorType);
}

/**
 * Handle session-related errors
 *
 * @param {string} sessionId - Session ID to validate
 * @param {string} createdAt - Optional session creation timestamp
 * @returns {object|null} Error object or null if valid
 */
export function handleSessionError(sessionId, createdAt = null) {
  // Validate session ID format
  if (!validateSessionId(sessionId)) {
    console.error('[ErrorHandler] Invalid session ID:', sessionId);
    return {
      errorType: ErrorType.INVALID_SESSION,
      message: getErrorMessage(ErrorType.INVALID_SESSION),
      action: getErrorAction(ErrorType.INVALID_SESSION),
      recoverable: false,
    };
  }

  // Check if session is expired (if timestamp provided)
  if (createdAt && isSessionExpired(createdAt)) {
    console.error('[ErrorHandler] Session expired:', { sessionId, createdAt });
    return {
      errorType: ErrorType.EXPIRED_SESSION,
      message: getErrorMessage(ErrorType.EXPIRED_SESSION),
      action: getErrorAction(ErrorType.EXPIRED_SESSION),
      recoverable: false,
    };
  }

  // Session is valid
  return null;
}

/**
 * Handle connection-related errors
 *
 * @param {string} errorMessage - Error message from connection attempt
 * @returns {object} Error object with type and details
 */
export function handleConnectionError(errorMessage) {
  const lowerMessage = (errorMessage || '').toLowerCase();

  // Detect room full error
  if (lowerMessage.includes('full') || lowerMessage.includes('two players')) {
    return {
      errorType: ErrorType.FULL_SESSION,
      message: getErrorMessage(ErrorType.FULL_SESSION),
      action: getErrorAction(ErrorType.FULL_SESSION),
      recoverable: false,
    };
  }

  // Detect network errors
  if (
    lowerMessage.includes('network') ||
    lowerMessage.includes('fetch') ||
    lowerMessage.includes('cors')
  ) {
    return {
      errorType: ErrorType.NETWORK_ERROR,
      message: getErrorMessage(ErrorType.NETWORK_ERROR),
      action: getErrorAction(ErrorType.NETWORK_ERROR),
      recoverable: true,
    };
  }

  // Detect connection loss/timeout
  if (
    lowerMessage.includes('timeout') ||
    lowerMessage.includes('disconnect') ||
    lowerMessage.includes('closed')
  ) {
    return {
      errorType: ErrorType.CONNECTION_LOST,
      message: getErrorMessage(ErrorType.CONNECTION_LOST),
      action: getErrorAction(ErrorType.CONNECTION_LOST),
      recoverable: true,
    };
  }

  // Generic error
  return {
    errorType: ErrorType.GENERIC_ERROR,
    message: getErrorMessage(ErrorType.GENERIC_ERROR),
    action: getErrorAction(ErrorType.GENERIC_ERROR),
    recoverable: true,
  };
}

/**
 * Create a retry handler with exponential backoff
 *
 * @param {object} options - Retry configuration
 * @param {number} options.maxAttempts - Maximum retry attempts (default: 5)
 * @param {number} options.initialDelay - Initial delay in ms (default: 1000)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 30000)
 * @param {function} options.onRetry - Callback when retry occurs
 * @returns {function} Retry handler function
 */
export function createRetryHandler(options = {}) {
  const {
    maxAttempts = MAX_RETRY_ATTEMPTS,
    initialDelay = INITIAL_RETRY_DELAY,
    maxDelay = MAX_RETRY_DELAY,
    onRetry = null,
  } = options;

  return async function retryHandler(operation) {
    let lastError = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // Attempt the operation
        const result = await operation();
        return result;
      } catch (error) {
        lastError = error;

        // Don't retry on last attempt
        if (attempt === maxAttempts - 1) {
          break;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          initialDelay * Math.pow(RETRY_BACKOFF_MULTIPLIER, attempt),
          maxDelay
        );

        console.log(
          `[ErrorHandler] Retry attempt ${attempt + 1}/${maxAttempts} after ${delay}ms`
        );

        // Call onRetry callback if provided
        if (onRetry) {
          onRetry(attempt + 1, error);
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // All attempts failed
    throw lastError;
  };
}
